import type { DecodedArray, SchemaElement } from "hyparquet";
import { ParquetWriter as HyparquetWriter } from "hyparquet-writer";
import sortedIndexBy from "lodash/sortedIndexBy";

import { type TypedArray, combineArrays } from "@/shared/array";

import { OPFSFileWriter } from "./OPFSFileWriter";
import type { Value } from "./ReaderLike";

export interface ParquetWriterOptions {
  /**
   * Whether to use compression.
   * Defaults to `false` to optimize for reads.
   */
  compressed: boolean;

  offset: number;

  /**
   * Size of each row group in the parquet file.
   * Use `ParquetWriter::calculateTargetRowGroupSize` to calculate a suitable value.
   * Defaults to 1_000 to optimize for reading small slices of data at a time without overfetching.
   */
  rowGroupSize: number;

  /**
   * Whether to calculate and write statistics for each column for each row group.
   * Defaults to `true`.
   */
  statistics: boolean;
}

interface QueuedWrite {
  data: Value[];
  rowStart: number;
  rowEnd: number;
}

export class ParquetWriter {
  private closed = false;
  private columnDef: SchemaElement[];
  private fileWriter: OPFSFileWriter;
  private nextRowStart: number;
  private rowGroupSize: number;
  private writer: HyparquetWriter;
  private writeQueue: QueuedWrite[];

  static create(
    fileHandle: FileSystemSyncAccessHandle,
    columnDef: SchemaElement | SchemaElement[],
    options?: Partial<ParquetWriterOptions>,
  ): ParquetWriter {
    return new ParquetWriter(fileHandle, columnDef, options);
  }

  static calculateTargetRowGroupSize(
    numRows: number,
    /**
     * More row groups allows for more efficient filtering, but increases the cost of reading the file's metadata.
     */
    maxRowGroupCount: number = 100,
    /**
     * Smaller row groups allow for finer-grained reads.
     */
    minRowGroupSize: number = 1_000,
  ): number {
    const maxRowGroupSize = Math.ceil(numRows / maxRowGroupCount);
    return Math.max(minRowGroupSize, maxRowGroupSize);
  }

  private constructor(
    fileHandle: FileSystemSyncAccessHandle,
    columnDef: SchemaElement | SchemaElement[],
    options?: Partial<ParquetWriterOptions>,
  ) {
    this.fileWriter = new OPFSFileWriter(fileHandle);
    this.nextRowStart = options?.offset ?? 0;
    this.rowGroupSize = options?.rowGroupSize ?? 1000;

    // Nested schemas are represented as a flat list of SchemaElements.
    // If the column to write is not part of a nested schema,
    // it can either be specified as a `SchemaElement` or as a single element array of `SchemaElement`.
    this.columnDef = Array.isArray(columnDef) ? columnDef : [columnDef];
    if (this.columnDef.length === 0) {
      throw new Error(
        "ParquetWriter requires a schema for the column to write.",
      );
    }

    this.writeQueue = [];

    const schema: SchemaElement[] = [
      {
        repetition_type: "REQUIRED",
        name: "schema",
        // Schema is a flat pre-order array: each parent is followed immediately
        // by the full subtree of each direct child (parent, child0 subtree, child1 subtree, ...).
        //
        // num_children is the number of immediate/direct children only (not the size of the whole subtree).
        // For the root, set num_children to the number of top-level fields.
        //
        // LIST/MAP use wrapper nodes (e.g. LIST -> repeated node -> element)
        //
        // Assume this is a single-column writer
        num_children: 1,
      },
      ...this.columnDef,
    ];

    this.writer = new HyparquetWriter({
      writer: this.fileWriter,
      schema,
      codec: options?.compressed ? "SNAPPY" : "UNCOMPRESSED",
      statistics: options?.statistics ?? true,
    });
  }

  public cancel(): void {
    if (this.closed === true) {
      return;
    }

    this.fileWriter.cancel();

    this.closed = true;
  }

  public close(): void {
    if (this.closed === true) {
      return;
    }

    this.flushWriteQueue();

    // Ensure any remaining data is written in a final row group
    this.fileWriter.flush();

    // Set chunk size to infinite to write metadata in one go
    this.fileWriter.chunkSize = Number.POSITIVE_INFINITY;
    this.writer.finish(); // will call `this.fileWriter.finish()`
    this.closed = true;
  }

  public queueWrite(data: Value[], rowStart: number, rowEnd: number): void {
    const item = { data, rowStart, rowEnd };
    const index = sortedIndexBy(this.writeQueue, item, (item) => item.rowStart);
    this.writeQueue.splice(index, 0, item);
    this.maybeFlushWriteQueue();
  }

  private flushWriteQueue(): void {
    if (this.writeQueue.length === 0) {
      return;
    }

    const chunks = [];
    while (this.writeQueue.length > 0) {
      const chunk = this.writeQueue.shift();
      if (chunk === undefined) {
        break;
      }
      chunks.push(chunk);
    }

    const data = combineArrays(...chunks.map((chunk) => chunk.data));
    this.write(data);
  }

  private maybeFlushWriteQueue() {
    const toWrite = this.topQueuedWriteItems();
    if (toWrite === 0) {
      return;
    }

    const chunks = [];
    for (let i = 0; i < toWrite; i++) {
      const chunk = this.writeQueue.shift()!;
      chunks.push(chunk);
    }

    const data = combineArrays(...chunks.map((chunk) => chunk.data));
    this.write(data);
  }

  private topQueuedWriteItems(): number {
    if (
      this.writeQueue.length === 0 ||
      this.writeQueue[0].rowStart !== this.nextRowStart
    ) {
      return 0;
    }

    let itemsToTake = 0;
    let lastRow = this.nextRowStart - 1;
    let rowCountQueued = 0;

    for (const item of this.writeQueue) {
      if (item.rowStart - lastRow > 1) {
        // Found a gap, so we stop.
        break;
      }

      itemsToTake++;
      lastRow = item.rowEnd;
      rowCountQueued += item.rowEnd - item.rowStart + 1;

      if (rowCountQueued >= this.rowGroupSize) {
        return itemsToTake;
      }
    }

    // If we have a continuous block of data from the start of the queue,
    // but it's less than a full row group, we can still decide to write it.
    // For now, let's only write when we have a full row group.
    return 0;
  }

  private write(data: Value[] | TypedArray): void {
    const name = this.columnDef.at(0)?.name;
    if (name === undefined) {
      throw new Error("ParquetWriter requires a column name to write.");
    }
    this.writer.write({
      columnData: [
        {
          name,
          data: data as DecodedArray,
        },
      ],
      rowGroupSize: this.rowGroupSize,
    });
    this.nextRowStart += data.length;
  }
}
